import information from "@/lib/public-information.json";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { 
  Calendar, Clock, Users, CreditCard, Bell, CheckCircle, ArrowRight,
  Smartphone, BarChart3, Zap, Coffee, CalendarX, GripVertical,
  UserCog, Layers, Mail, Settings, CalendarDays, Scissors, Heart,
  Stethoscope, Dumbbell, Briefcase, Quote
} from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import PricingSection from "@/components/PricingSection";

const LandingPage = () => {
  const { t } = useTranslation();
  
  const mainFeatures = [
    {
      icon: Calendar,
      title: "Agenda Intelligente",
      description: "Visualizza prenotazioni per giorno, settimana o per operatore. Trascina gli appuntamenti per riprogrammarli."
    },
    {
      icon: Users,
      title: "Gestione Collaboratori",
      description: "Ogni membro del team ha il proprio calendario, servizi assegnati e accesso dedicato."
    },
    {
      icon: Coffee,
      title: "Pause e Indisponibilità",
      description: "Imposta pause pranzo, ferie e giorni di chiusura. Il sistema blocca automaticamente le prenotazioni."
    },
    {
      icon: CreditCard,
      title: "Pagamenti Online",
      description: "Richiedi acconti o pagamenti anticipati per ridurre i no-show. Integrazione sicura con Stripe."
    },
    {
      icon: Bell,
      title: "Promemoria Automatici",
      description: "I clienti ricevono conferma e promemoria prima dell'appuntamento. Meno telefonate."
    },
    {
      icon: CalendarDays,
      title: "Sincronizza con Google",
      description: "Le prenotazioni appaiono automaticamente sul tuo Google Calendar. Sempre aggiornato."
    }
  ];

  const advancedFeatures = [
    {
      icon: Layers,
      title: "Categorie e Listino",
      description: "Organizza i servizi per categoria. I clienti trovano subito ciò che cercano."
    },
    {
      icon: GripVertical,
      title: "Ordina come vuoi",
      description: "Decidi l'ordine dei servizi nel listino. Metti in evidenza i più richiesti."
    },
    {
      icon: Smartphone,
      title: "Perfetto su Mobile",
      description: "I clienti prenotano da smartphone. Tu gestisci tutto anche dal telefono."
    },
    {
      icon: BarChart3,
      title: "Report e Statistiche",
      description: "Scopri quali servizi vanno di più e l'andamento del fatturato."
    },
    {
      icon: Clock,
      title: "Disponibilità Flessibile",
      description: "Orari diversi per ogni giorno, eccezioni per festività, turni personalizzati."
    },
    {
      icon: Mail,
      title: "Email Personalizzate",
      description: "Personalizza i messaggi di conferma e promemoria con il tuo stile."
    }
  ];

  const staffFeatures = [
    {
      icon: UserCog,
      title: "Accesso Dedicato",
      description: "Ogni collaboratore accede con le proprie credenziali e vede solo la propria agenda."
    },
    {
      icon: CalendarX,
      title: "Gestione Assenze",
      description: "Ferie, malattia, permessi: blocca le disponibilità in autonomia."
    },
    {
      icon: Settings,
      title: "Servizi Assegnati",
      description: "Ogni collaboratore offre solo i servizi per cui è qualificato."
    }
  ];

  const industries = [
    { icon: Scissors, name: "Parrucchieri", description: "Saloni e barbershop" },
    { icon: Heart, name: "Centri Estetici", description: "Spa e wellness" },
    { icon: Stethoscope, name: "Studi Medici", description: "Ambulatori e cliniche" },
    { icon: Dumbbell, name: "Personal Trainer", description: "Fitness e coaching" },
    { icon: Briefcase, name: "Consulenti", description: "Professionisti e studi" },
    { icon: Users, name: "E molto altro", description: "Attività su appuntamento" }
  ];

  const testimonials = [
    {
      quote: "Finalmente un sistema semplice! I clienti prenotano da soli e non perdo più tempo al telefono.",
      author: "Marina C.",
      role: "Titolare salone"
    },
    {
      quote: "I promemoria automatici hanno dimezzato i no-show. Un investimento che si ripaga da solo.",
      author: "Luca R.",
      role: "Centro estetico"
    },
    {
      quote: "Gestisco 5 collaboratori senza stress. Ognuno vede la sua agenda e i clienti sono soddisfatti.",
      author: "Francesca M.",
      role: "Hair stylist"
    }
  ];

  const businessBenefits = [
    { stat: "50%", label: "Meno telefonate" },
    { stat: "24/7", label: "Prenotazioni attive" },
    { stat: "-70%", label: "No-show" },
    { stat: "100%", label: "Sincronizzato" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-bold text-lg text-slate-900">FacilePrenotare</span>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <a href="#funzionalita" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                Funzionalità
              </a>
              <a href="#settori" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                Per chi è
              </a>
              <a href="#pricing" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                Prezzi
              </a>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="login-nav-btn">Accedi</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="register-nav-btn">Prova Gratis</Button>
              </Link>
            </nav>
            
            <div className="md:hidden flex items-center gap-2">
              <Link to="/login">
                <Button size="sm" variant="ghost" data-testid="mobile-login-btn">Accedi</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="mobile-register-btn">Prova</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2.5 py-1 rounded-full text-xs font-medium mb-4">
                <Zap className="h-3 w-3" />
                Il gestionale per le tue prenotazioni
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                FacilePrenotare: dì addio alla{" "}
                <span className="text-primary">agenda cartacea</span>
              </h1>
              <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
                I tuoi clienti prenotano online in qualsiasi momento, tu gestisci appuntamenti e collaboratori 
                da un'unica dashboard. Meno telefonate, meno errori, più tempo per il tuo lavoro.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link to="/register">
                  <Button size="default" className="w-full sm:w-auto gap-2" data-testid="hero-cta-btn">
                    Inizia la prova gratuita
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <a href="#funzionalita">
                  <Button size="default" variant="outline" className="w-full sm:w-auto" data-testid="hero-features-btn">
                    Scopri come funziona
                  </Button>
                </a>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Nessuna carta richiesta • Attivo in 5 minuti • Assistenza inclusa
              </p>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-xl border border-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&h=600&fit=crop" 
                  alt="Salone professionale"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-3 -left-3 bg-white p-3 rounded-lg shadow-lg border border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-900">Nuovo appuntamento</p>
                    <p className="text-[10px] text-slate-500">Laura B. - Taglio + Piega, 14:30</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 bg-white p-2 rounded-lg shadow-lg border border-slate-100">
                <div className="flex items-center gap-1.5">
                  <Bell className="h-3 w-3 text-amber-500" />
                  <span className="text-[10px] font-medium text-slate-700">Promemoria inviato</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {businessBenefits.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">
                  {item.stat}
                </div>
                <p className="text-slate-400 text-xs sm:text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section id="funzionalita" className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Tutto quello che ti serve
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl mx-auto">
              Strumenti pensati per chi lavora su appuntamento, semplici da usare fin dal primo giorno
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mainFeatures.map((feature, index) => (
              <div 
                key={index}
                className="p-5 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <feature.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Management Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-sm text-slate-900">Agenda di oggi</h4>
                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">3 appuntamenti</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border-l-3 border-blue-500">
                    <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">09:00 - Taglio Uomo</p>
                      <p className="text-xs text-slate-500 truncate">Marco Rossi • 30 min</p>
                    </div>
                    <span className="text-xs font-medium text-blue-600">€25</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border-l-3 border-amber-500">
                    <Coffee className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">13:00 - Pausa pranzo</p>
                      <p className="text-xs text-slate-500">1 ora</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border-l-3 border-purple-500">
                    <Clock className="h-4 w-4 text-purple-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">15:00 - Colore + Taglio</p>
                      <p className="text-xs text-slate-500 truncate">Giulia Bianchi • 90 min</p>
                    </div>
                    <span className="text-xs font-medium text-purple-600">€85</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                Hai un team?<br/>Nessun problema.
              </h2>
              <p className="mt-3 text-sm text-slate-600">
                Ogni collaboratore ha il proprio accesso per consultare l'agenda. Tu mantieni il controllo su tutto.
              </p>
              
              <div className="mt-6 space-y-4">
                {staffFeatures.map((feature, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">{feature.title}</h4>
                      <p className="text-xs text-slate-600 mt-0.5">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="settori" className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Pensato per il tuo settore
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl mx-auto">
              Che tu sia un parrucchiere, un estetista o un professionista, FacilePrenotare si adatta alle tue esigenze
            </p>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {industries.map((industry, index) => (
              <div 
                key={index} 
                className="text-center p-4 rounded-xl border border-slate-100 hover:border-primary/30 hover:shadow-sm transition-all duration-300"
              >
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2">
                  <industry.icon className="h-5 w-5 text-slate-700" />
                </div>
                <h3 className="font-semibold text-slate-900 text-xs">{industry.name}</h3>
                <p className="text-slate-500 text-[10px] mt-0.5">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              E tanto altro ancora
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl mx-auto">
              Funzionalità avanzate per far crescere la tua attività
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {advancedFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-4 rounded-lg border border-slate-100 hover:shadow-sm transition-all duration-200"
              >
                <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center mb-3">
                  <feature.icon className="h-4 w-4 text-slate-700" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">
                  {feature.title}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Chi lo usa, lo consiglia
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-slate-50 p-5 rounded-xl">
                <Quote className="h-6 w-6 text-primary/30 mb-3" />
                <p className="text-sm text-slate-700 leading-relaxed mb-4 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-xs text-slate-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Pronto in 3 passi
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Inizia a ricevere prenotazioni oggi stesso
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                step: "1",
                title: "Crea il profilo",
                description: "Inserisci i dati della tua attività, gli orari di apertura e i servizi che offri."
              },
              {
                step: "2",
                title: "Aggiungi il team",
                description: "Se hai collaboratori, crea il loro profilo e assegna i servizi."
              },
              {
                step: "3",
                title: "Condividi il link",
                description: "Pubblica il link sul tuo profilo social. I clienti iniziano a prenotare subito."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-5 rounded-xl shadow-sm">
                <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Pricing Section */}
      <PricingSection />

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            Pronto a semplificare il tuo lavoro?
          </h2>
          <p className="mt-3 text-sm text-slate-300 max-w-2xl mx-auto">
            Prova FacilePrenotare gratis per 14 giorni. Nessun impegno, nessuna carta di credito.
          </p>
          <div className="mt-6">
            <Link to="/register">
              <Button size="default" className="gap-2 bg-white text-slate-900 hover:bg-slate-100" data-testid="cta-register-btn">
                Inizia la prova gratuita
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-400">
            Attivo in 5 minuti • Supporto via email • Disdici quando vuoi
          </p>
        </div>
      </section>

      <section id="google-calendar" className="py-12 px-6 bg-slate-50"><div className="max-w-4xl mx-auto"><h2 className="text-2xl font-bold mb-4">FacilePrenotare e Google Calendar</h2><p className="mb-4 leading-relaxed">{information.purpose}</p><p className="mb-4 leading-relaxed">{information.googlePurpose}</p><p className="mb-4 leading-relaxed">Dopo il tuo consenso, leggiamo l’email Google per identificare l’account e i calendari disponibili per permetterti di scegliere. Nel calendario selezionato leggiamo gli eventi e gli intervalli occupati; gli eventi delle prenotazioni possono includere cliente, servizi, operatore, contatti, note e orari. Il collegamento è facoltativo e puoi disconnetterlo o revocarlo.</p><p><Link className="text-primary underline" to="/privacy">Leggi come trattiamo, conserviamo e cancelliamo i dati Google</Link></p><p className="mt-4 text-sm">Un servizio di Strategyfor Srl · <a className="underline" href="mailto:info@appstream.it">info@appstream.it</a></p></div></section>
      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
            {/* Logo & Description */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <span className="font-bold text-base text-slate-900">FacilePrenotare</span>
              </div>
              <p className="text-xs text-slate-500 max-w-sm">
                Il gestionale italiano per le prenotazioni online. 
                Semplice, professionale, pensato per chi lavora su appuntamento.
              </p>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-sm text-slate-900 mb-3">Prodotto</h4>
              <ul className="space-y-1.5 text-xs">
                <li>
                  <a href="#funzionalita" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Funzionalità
                  </a>
                </li>
                <li>
                  <a href="#settori" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Per chi è
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Prezzi
                  </a>
                </li>
                <li>
                  <Link to="/register" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Prova Gratis
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-sm text-slate-900 mb-3">Informazioni</h4>
              <ul className="space-y-1.5 text-xs">
                <li>
                  <Link to="/privacy" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Termini di Servizio
                  </Link>
                </li>
                <li>
                  <a href="mailto:info@appstream.it" className="text-slate-500 hover:text-slate-900 transition-colors">
                    Contattaci
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom */}
          <div className="pt-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              © 2026 FacilePrenotare. Tutti i diritti riservati.
            </p>
            <div className="flex items-center gap-4">
              <LanguageSelector />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
